import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Wrapper around React.lazy that auto-recovers from ``ChunkLoadError``.
 *
 * ChunkLoadError happens when a user's browser still has the old
 * ``index.html`` referencing a chunk hash that no longer exists on the CDN
 * (classic after-deploy problem with hashed Vite bundles). The symptom is
 * a broken screen mid-navigation.
 *
 * Strategy:
 * 1. Try the dynamic import.
 * 2. On failure, check whether the error looks like a chunk load failure
 *    (``ChunkLoadError``, ``Loading chunk`` in the message, or
 *    ``Failed to fetch dynamically imported module``).
 * 3. If it does, use a sessionStorage flag to perform exactly ONE full
 *    reload so infinite loops are impossible — if the reload still fails,
 *    the error bubbles up to the ErrorBoundary.
 * 4. Non-chunk errors are re-thrown unchanged.
 */

const RELOAD_FLAG_KEY = 'janus_chunk_reload_attempted';

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  if (err.name === 'ChunkLoadError') return true;
  const msg = err.message || '';
  return (
    msg.includes('Loading chunk') ||
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module')
  );
}

export function reloadOnChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  try {
    const alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (alreadyReloaded) {
      // We already reloaded once — something else is wrong, give up so
      // the ErrorBoundary can show the user a real error.
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode / cookies disabled).
    // Reload anyway — worst case is one extra reload.
  }
  window.location.reload();
  return true;
}

export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Drop-in replacement for ``React.lazy`` that auto-reloads on ChunkLoadError.
 *
 * Usage::
 *
 *     const DashboardPage = lazyWithRetry(() => import('./DashboardPage'));
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy<T>(async () => {
    try {
      const mod = await factory();
      // Successful load — clear the reload flag so a future chunk error
      // in the same session can still trigger one reload.
      clearChunkReloadFlag();
      return mod;
    } catch (error) {
      if (reloadOnChunkError(error)) {
        // Reload is in flight — return a promise that never resolves so React
        // keeps showing the Suspense fallback until the page unloads.
        return new Promise(() => {}) as Promise<{ default: T }>;
      }
      throw error;
    }
  });
}
